import * as apiDelete from 'api/report/Deletev1filesdeleteV1';
import * as apiGet from 'api/report/Getv1filesdownloadV1';
import * as ReportGroup from 'containers/desktop/Reports/types/ReportGroup';
import * as ReportList from 'containers/desktop/Reports/types/ReportList';
import { StringList } from 'containers/desktop/Reports/types/StringList';

export type Type = ReportGroup.Type[];

export function create(): Type {
	return [];
}

const getMonthKey = (dateStart: string) => {
	if (!dateStart || !dateStart.includes('-')) {
		return '';
	}
	const parts = dateStart.split('-');
	if (parts.length < 2) {
		return '';
	}
	return `${parts[0]}-${parts[1]}`;
};

export function createFromReportList(list: ReportList.Type): Type {
	const reportGroups: Type = [];

	for (const report of list) {
		const monthKey = getMonthKey(report.dateStart);
		if (!monthKey) continue;

		const reportGroup = reportGroups.find((rg) => rg.id === monthKey);
		if (reportGroup) {
			reportGroup.reports.push(report);
			reportGroup.reports.sort((a, b) => {
				const dateStartCompare = b.dateStart.localeCompare(a.dateStart);
				if (dateStartCompare !== 0) return dateStartCompare;
				return b.uploadedAt.getTime() - a.uploadedAt.getTime();
			});
		} else {
			const monthDate = new Date(monthKey + '-01');
			reportGroups.push({
				...ReportGroup.create(),
				id: monthKey,
				date: monthDate,
				reports: [report],
			});
		}
	}
	return reportGroups;
}

export function filterBySearch(list: Type, search: string): Type {
	if (!search.trim()) return list;
	const term = search.trim().toLowerCase();
	return list
		.map((rg) => ({
			...rg,
			reports: rg.reports.filter(
				(r) => r.id.toLowerCase().includes(term) || r.username?.toLowerCase().includes(term)
			),
		}))
		.filter((rg) => rg.reports.length > 0);
}

export function filterByStatus(list: Type, status: string): Type {
	if (status === 'all' || !status) return list;
	return list
		.map((rg) => ({
			...rg,
			reports: rg.reports.filter((r) => r.status === status),
		}))
		.filter((rg) => rg.reports.length > 0);
}

export function filterByMonth(list: Type, month: string): Type {
	if (!month) return list;
	return list.filter((rg) => rg.id === month);
}

export function countReports(list: Type): number {
	return list.reduce((acc, rg) => acc + rg.reports.length, 0);
}

export function paginate(list: Type, page: number, pageSize: number): Type {
	const start = (page - 1) * pageSize;
	const end = start + pageSize;
	return list.slice(start, end);
}

export function toggleSelectOfReportGroup(list: Type, id: string): Type {
	return list.map((reportGroup) => {
		if (reportGroup.id === id) {
			return ReportGroup.toggleSelect(reportGroup);
		}
		return reportGroup;
	});
}

export function toggleSelectOfReport(list: Type, groupId: string, reportId: string): Type {
	return list.map((reportGroup) => {
		if (reportGroup.id === groupId) {
			return ReportGroup.toggleSelectOfReport(reportGroup, reportId);
		}
		return reportGroup;
	});
}

export function orderByDate(list: Type): Type {
	return list.sort((a, b) => b.date.getTime() - a.date.getTime());
}

export function getAllSelectedReportIds(list: Type): string[] {
	const selectedReportIds: string[] = [];
	for (const reportGroup of list) {
		for (const report of reportGroup.reports) {
			if (!report.isSelected) continue;
			selectedReportIds.push(report.id);
		}
	}
	return StringList.distinct(selectedReportIds);
}

export async function deleteFromApi(list: Type): Promise<void> {
	const reportIds = getAllSelectedReportIds(list);
	await apiDelete.callApi({
		requestBody: {
			fileIds: reportIds,
		},
	});
}

export async function downloadFromApi(list: Type): Promise<void> {
	const reportIds = getAllSelectedReportIds(list);
	try {
		const blob = await apiGet.callApi({
			requestBody: {
				fileIds: reportIds,
				fileType: 'report',
			},
		});

		if (!blob) return;

		const fileURL = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = fileURL;
		link.download = 'archive.zip';
		link.click();
		URL.revokeObjectURL(fileURL);
	} catch (error) {
		console.error('Fehler beim Download:', error);
	}
}

export async function downloadSingleBlob(reportId: string, fileType: string = 'report'): Promise<Blob | null> {
	try {
		const blob = await apiGet.callApi({
			requestBody: {
				fileIds: [reportId],
				fileType: fileType,
			},
		});
		return blob ?? null;
	} catch (error) {
		console.error('Error downloading file:', error);
		return null;
	}
}

export async function downloadSingle(reportId: string): Promise<void> {
	const blob = await downloadSingleBlob(reportId);
	if (!blob) return;

	const fileURL = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = fileURL;
	link.download = `${reportId}.zip`;
	link.click();
	URL.revokeObjectURL(fileURL);
}

export async function deleteSingle(reportId: string): Promise<void> {
	await apiDelete.callApi({
		requestBody: {
			fileIds: [reportId],
		},
	});
}
